import React, { useState, useEffect, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TableSortLabel, TextField, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText,
  Typography, Box, Chip, CircularProgress, MenuItem, Select, FormControl, InputLabel
} from "@mui/material";

interface DetectorCount {
  detector: string;
  count: number;
}

interface TrainData {
  trainNumber: string;
  modelNumber: string;
  avgRssi: number;
  quota: number;
  trend: string;
  resendRate: number;
  resendSpikes: number;
  worstDetector: string;
  resendsPerDetector: DetectorCount[];
  tripsWithoutResendFlag: number;
  tripsWithResendFlag: number;
  problem_score: number;
}

interface Props {
  data: TrainData[];
}

const getScoreCategory = (score: number): { label: string; color: "error" | "warning" | "success" } => {
  if (score >= 80) return { label: "High Risk", color: "error" };
  if (score >= 50) return { label: "Moderate", color: "warning" };
  return { label: "Safe", color: "success" };
};

const TrainResendTable: React.FC<Props> = ({ data }) => {
  const [orderBy, setOrderBy] = useState<keyof TrainData>("trainNumber");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [search, setSearch] = useState("");
  const [dialogData, setDialogData] = useState<DetectorCount[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [visibleRows, setVisibleRows] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState("All");

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLTableRowElement | null>(null);

  const handleRequestSort = (property: keyof TrainData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filtered = data.filter(item => {
    const cat = getScoreCategory(item.problem_score).label;
    const matchesCategory = categoryFilter === "All" || categoryFilter === cat;
    return (
      matchesCategory && (
        item.trainNumber.toLowerCase().includes(search.toLowerCase()) ||
        item.modelNumber.toLowerCase().includes(search.toLowerCase()) ||
        item.worstDetector.toLowerCase().includes(search.toLowerCase())
      )
    );
  });

  const sorted = filtered.sort((a, b) => {
    const aVal = a[orderBy];
    const bVal = b[orderBy];
    return (aVal < bVal ? -1 : 1) * (order === "asc" ? 1 : -1);
  });

  const topWorstFiltered = sorted.sort((a, b) => b.problem_score - a.problem_score);
  const visibleData = showAll ? topWorstFiltered.slice(0, visibleRows) : topWorstFiltered.slice(0, 10);

  useEffect(() => {
    if (!showAll || !loadMoreRef.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleRows(prev => Math.min(prev + 10, topWorstFiltered.length));
      }
    });
    observer.current.observe(loadMoreRef.current);
  }, [showAll, visibleRows, topWorstFiltered.length]);

  const formatPreview = (detectors: DetectorCount[]) => {
    const preview = detectors.slice(0, 3).map(d => `${d.detector} (${d.count})`).join(", ");
    const more = detectors.length > 3 ? ` +${detectors.length - 3} more` : "";
    return preview + more;
  };

  return (
    <Paper>
      <Box display="flex" gap={1} p={2} alignItems="center">
        <Box flex={5}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box flex={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="High Risk">High Risk</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Safe">Safe</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box flex={2}>
          <Button fullWidth variant="outlined" onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show Top 10" : "Show All"}
          </Button>
        </Box>
      </Box>
      <TableContainer sx={{ maxHeight: 750, overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {["Train", "Model", "avgRssi", "trend", "Resend Rate %", "det:WithFlag",
                "det:WithoutFlag", "worstDetector", "ResendsPerDetector", "Score", "Category"].map((key) => (
                <TableCell key={key} sortDirection={orderBy === key ? order : false}>
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleRequestSort(key as keyof TrainData)}
                  >
                    {key}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleData.map((row, index) => {
              const category = getScoreCategory(row.problem_score);
              return (
                <TableRow key={index}>
                  <TableCell>{row.trainNumber}</TableCell>
                  <TableCell>{row.modelNumber}</TableCell>
                  <TableCell>{row.avgRssi}</TableCell>
                  <TableCell>{row.trend}</TableCell>
                  <TableCell>{row.resendRate}</TableCell>
                  <TableCell>{row.tripsWithResendFlag}</TableCell>
                  <TableCell>{row.tripsWithoutResendFlag}</TableCell>
                  <TableCell>{row.worstDetector}</TableCell>
                  <TableCell>
                    <Tooltip title={row.resendsPerDetector.map(d => `${d.detector} (${d.count})`).join(", ")} placement="top">
                      <span>{formatPreview(row.resendsPerDetector)}</span>
                    </Tooltip>
                    <Button size="small" onClick={() => setDialogData(row.resendsPerDetector)}>View</Button>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Score = -0.6*TripsWithFlag, -0.5*Quota, -0.4*Spikes, -0.3*Gap, -0.2*Rate, +0.1*RSSI, +0.2*TripsWithoutFlag`}>
                      <span>{row.problem_score?.toFixed(2)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={category.label} color={category.color} size="small" />
                  </TableCell>
                </TableRow>
              );
            })}
            {showAll && visibleRows < topWorstFiltered.length && (
              <TableRow ref={loadMoreRef}>
                <TableCell colSpan={11} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={!!dialogData} onClose={() => setDialogData(null)}>
        <DialogTitle>Resends Per Detector</DialogTitle>
        <DialogContent>
          <List>
            {dialogData?.map((d, idx) => (
              <ListItem key={idx} sx={{ bgcolor: '#f5f5f5', mb: 1, borderRadius: 2, px: 2, py: 1 }}>
                <ListItemText
                  primary={<Typography variant="body1" fontWeight="bold" color="primary">{d.detector}</Typography>}
                  secondary={<Typography variant="body2" color="text.secondary">Resend Count: {d.count}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default TrainResendTable;
